import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { db } from "../firebase";
import { ref, onValue, push, update, get, remove } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { uploadImage } from "../utils/uploadImage";
import { sendPushNotification } from "../utils/notifications";
import {
  ArrowLeft,
  Send,
  MessageCircle,
  ShoppingBag,
  Image as ImageIcon,
  Trash2,
  Search,
  X,
} from "lucide-react";

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" });
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
}

export default function Chat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chatMeta, setChatMeta] = useState(null);
  const [text, setText] = useState("");
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sendError, setSendError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const imageInputRef = useRef(null);
  // Profile photos cache: { [uid]: photoURL | null }
  const [userPhotos, setUserPhotos] = useState({});
  // Photo privacy cache: { [uid]: "everyone" | "contacts" | "nobody" | "custom" }
  const [userPrivacy, setUserPrivacy] = useState({});
  // Photo of the other person in an open chat
  const [otherUserPhoto, setOtherUserPhoto] = useState(null);
  const [otherUserPrivacy, setOtherUserPrivacy] = useState("everyone");

  const [replyTo, setReplyTo] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ----- Conversations list listener via userChats index -----
  useEffect(() => {
    if (!currentUser) return;
    const userChatsRef = ref(db, `userChats/${currentUser.uid}`);
    const unsub = onValue(userChatsRef, async (snapshot) => {
      const indexedIds = snapshot.exists() ? Object.keys(snapshot.val()) : [];

      // Also scan for chats where user is seller but index may be missing
      let sellerChatIds = [];
      try {
        const sellerChatsRef = ref(db, `sellerChats/${currentUser.uid}`);
        const sellerSnap = await get(sellerChatsRef);
        if (sellerSnap.exists()) {
          sellerChatIds = Object.keys(sellerSnap.val());
        }
      } catch (e) {}

      const allIds = [...new Set([...indexedIds, ...sellerChatIds])];

      if (allIds.length === 0) {
        setConversations([]);
        setLoadingChats(false);
        return;
      }

      const fetched = await Promise.all(
        allIds.map(async (cid) => {
          try {
            const snap = await get(ref(db, `chats/${cid}`));
            if (snap.exists()) return { id: cid, ...snap.val() };
          } catch (e) {}
          return null;
        })
      );
      const myChats = fetched
        .filter(Boolean)
        .sort((a, b) => {
          const aTime = a.lastAt ? new Date(a.lastAt) : new Date(0);
          const bTime = b.lastAt ? new Date(b.lastAt) : new Date(0);
          return bTime - aTime;
        });
      setConversations(myChats);
      setLoadingChats(false);
    });
    return () => unsub();
  }, [currentUser]);

  // ----- Individual chat messages listener -----
  useEffect(() => {
    if (!chatId || !currentUser) return;
    setLoadingMessages(true);

    // Load chat meta
    const metaRef = ref(db, `chats/${chatId}`);
    get(metaRef).then((snap) => {
      if (snap.exists()) {
        setChatMeta({ id: chatId, ...snap.val() });
      }
    });

    // Listen to messages
    const msgsRef = ref(db, `chats/${chatId}/messages`);
    const unsub = onValue(msgsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const msgList = Object.entries(data)
        .map(([id, msg]) => ({ id, ...msg }))
        .sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      setMessages(msgList);
      setLoadingMessages(false);

      // Mark unread messages from the other person as read
      const updates = {};
      msgList.forEach((msg) => {
        if (msg.senderId !== currentUser.uid && msg.read === false) {
          updates[`chats/${chatId}/messages/${msg.id}/read`] = true;
        }
      });
      if (Object.keys(updates).length > 0) {
        update(ref(db), updates);
      }
    });

    return () => unsub();
  }, [chatId, currentUser]);

  // ----- Typing indicator listener -----
  useEffect(() => {
    if (!chatId || !currentUser) return;
    const typingRef = ref(db, `chats/${chatId}/typing`);
    const unsub = onValue(typingRef, (snap) => {
      const data = snap.val() || {};
      const now = Date.now();
      const active = {};
      Object.entries(data).forEach(([uid, info]) => {
        if (uid !== currentUser.uid && info?.at && now - info.at < 4000) {
          active[uid] = info.name;
        }
      });
      setTypingUsers(active);
    });
    return () => unsub();
  }, [chatId, currentUser]);

  // Clean up typing status when leaving chat
  useEffect(() => {
    if (!chatId || !currentUser) return;
    return () => {
      update(ref(db), { [`chats/${chatId}/typing/${currentUser.uid}`]: null }).catch(() => {});
      clearTimeout(typingTimeoutRef.current);
    };
  }, [chatId, currentUser]);

  // ----- Auto-scroll to bottom -----
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // ----- Send message -----
  async function handleSend(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !chatId || !currentUser || sending) return;

    setSending(true);
    const now = new Date().toISOString();
    const senderName =
      userProfile?.name || currentUser.displayName || "Unknown";

    // Clear typing indicator immediately on send
    update(ref(db), { [`chats/${chatId}/typing/${currentUser.uid}`]: null }).catch(() => {});
    clearTimeout(typingTimeoutRef.current);

    try {
      const newMsgRef = ref(db, `chats/${chatId}/messages`);
      const msgData = {
        text: trimmed,
        senderId: currentUser.uid,
        senderName,
        createdAt: now,
        read: false,
      };
      if (replyTo) {
        msgData.replyTo = {
          id: replyTo.id,
          text: (replyTo.text || "").substring(0, 80),
          senderName: replyTo.senderName,
          type: replyTo.type || "text",
        };
      }
      await push(newMsgRef, msgData);

      // Update chat meta lastMessage / lastAt
      await update(ref(db, `chats/${chatId}`), {
        lastMessage: trimmed,
        lastAt: now,
      });

      setText("");
      setReplyTo(null);
      setSendError("");
      if (inputRef.current) inputRef.current.focus();

      // Push notification to recipient (fire-and-forget)
      if (chatMeta) {
        const recipientUid = chatMeta.buyerId === currentUser.uid ? chatMeta.sellerId : chatMeta.buyerId;
        get(ref(db, `users/${recipientUid}/fcmToken`)).then((snap) => {
          if (snap.exists()) {
            sendPushNotification({
              token: snap.val(),
              title: senderName,
              body: trimmed.length > 100 ? trimmed.slice(0, 100) + "..." : trimmed,
              url: `/chat/${chatId}`,
            });
          }
        }).catch(() => {});
      }
    } catch (err) {
      setSendError("Failed to send. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  }

  // ----- Handle text input with typing indicator -----
  function handleTyping(e) {
    setText(e.target.value);
    if (!chatId || !currentUser) return;
    const name = userProfile?.name || currentUser.displayName || "Someone";
    update(ref(db), { [`chats/${chatId}/typing/${currentUser.uid}`]: { name, at: Date.now() } }).catch(() => {});
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      update(ref(db), { [`chats/${chatId}/typing/${currentUser.uid}`]: null }).catch(() => {});
    }, 3000);
  }

  // ----- Quick message -----
  async function sendQuickMessage(quickText) {
    if (!chatId || !currentUser || sending) return;
    setSending(true);
    const now = new Date().toISOString();
    const senderName = userProfile?.name || currentUser.displayName || "Unknown";
    try {
      await push(ref(db, `chats/${chatId}/messages`), {
        text: quickText,
        senderId: currentUser.uid,
        senderName,
        createdAt: now,
        read: false,
      });
      await update(ref(db, `chats/${chatId}`), { lastMessage: quickText, lastAt: now });
    } catch (err) { setSendError("Failed to send. Try again."); }
    setSending(false);
  }

  // ----- Image send -----
  async function handleImageSend(e) {
    const file = e.target.files[0];
    if (!file || !chatId || !currentUser) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5MB."); return; }
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      const now = new Date().toISOString();
      const senderName = userProfile?.name || currentUser.displayName || "Unknown";
      await push(ref(db, `chats/${chatId}/messages`), {
        text: url,
        type: "image",
        senderId: currentUser.uid,
        senderName,
        createdAt: now,
        read: false,
      });
      await update(ref(db, `chats/${chatId}`), { lastMessage: "📷 Image", lastAt: now });
      if (chatMeta) {
        const recipientUid = chatMeta.buyerId === currentUser.uid ? chatMeta.sellerId : chatMeta.buyerId;
        get(ref(db, `users/${recipientUid}/fcmToken`)).then((snap) => {
          if (snap.exists()) {
            sendPushNotification({
              token: snap.val(),
              title: senderName,
              body: "📷 Sent an image",
              url: `/chat/${chatId}`,
            });
          }
        }).catch(() => {});
      }
    } catch (err) {  alert("Failed to send image."); }
    setUploadingImage(false);
    if (imageInputRef.current) imageInputRef.current.value = "";
  }

  // ----- Delete chat -----
  async function deleteChat(chatId, e) {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
    await remove(ref(db, `chats/${chatId}`));
    setConversations(prev => prev.filter(c => c.id !== chatId));
  }

  // ----- Helpers -----
  function getOtherPersonName(chat) {
    if (!currentUser) return "";
    return currentUser.uid === chat.buyerId ? chat.sellerName : chat.buyerName;
  }

  function hasUnread(chat) {
    // We can't efficiently check this without reading all messages,
    // so we just rely on a simple heuristic from conversation data.
    return false; // Unread badge is shown per-message in chat view
  }

  function getUnreadCount(chatMessages, uid) {
    return chatMessages.filter(
      (m) => m.senderId !== uid && m.read === false
    ).length;
  }

  // ----- Conversation unread state (listen per-chat, not bulk) -----
  const [unreadMap, setUnreadMap] = useState({});

  useEffect(() => {
    if (!currentUser || conversations.length === 0) return;
    const listeners = [];
    conversations.forEach((chat) => {
      const msgsRef = ref(db, `chats/${chat.id}/messages`);
      const unsub = onValue(msgsRef, (snap) => {
        const data = snap.val() || {};
        const count = Object.values(data).filter(
          (m) => m.senderId !== currentUser.uid && m.read === false
        ).length;
        setUnreadMap((prev) => ({ ...prev, [chat.id]: count }));
      });
      listeners.push(unsub);
    });
    return () => listeners.forEach((u) => u());
  }, [conversations.length, currentUser]);

  // Fetch profile photos + privacy for conversation list avatars
  useEffect(() => {
    if (!currentUser || conversations.length === 0) return;
    const uids = new Set();
    conversations.forEach(chat => {
      const otherUid = chat.buyerId === currentUser.uid ? chat.sellerId : chat.buyerId;
      if (otherUid && userPhotos[otherUid] === undefined) uids.add(otherUid);
    });
    uids.forEach(uid => {
      get(ref(db, `users/${uid}`)).then(snap => {
        if (snap.exists()) {
          const d = snap.val();
          setUserPhotos(prev => ({ ...prev, [uid]: d.photoURL || null }));
          setUserPrivacy(prev => ({ ...prev, [uid]: d.photoPrivacy || "everyone" }));
        } else {
          setUserPhotos(prev => ({ ...prev, [uid]: null }));
        }
      }).catch(() => {
        setUserPhotos(prev => ({ ...prev, [uid]: null }));
      });
    });
  }, [conversations, currentUser]); // eslint-disable-line

  // Fetch photo + privacy for current open chat's other user
  useEffect(() => {
    if (!chatMeta || !currentUser) return;
    const otherUid = chatMeta.buyerId === currentUser.uid ? chatMeta.sellerId : chatMeta.buyerId;
    if (!otherUid) return;
    if (userPhotos[otherUid] !== undefined) {
      setOtherUserPhoto(userPhotos[otherUid]);
      setOtherUserPrivacy(userPrivacy[otherUid] || "everyone");
      return;
    }
    get(ref(db, `users/${otherUid}`)).then(snap => {
      if (snap.exists()) {
        const d = snap.val();
        const photo = d.photoURL || null;
        const privacy = d.photoPrivacy || "everyone";
        setOtherUserPhoto(photo);
        setOtherUserPrivacy(privacy);
        setUserPhotos(prev => ({ ...prev, [otherUid]: photo }));
        setUserPrivacy(prev => ({ ...prev, [otherUid]: privacy }));
      } else {
        setOtherUserPhoto(null);
      }
    }).catch(() => setOtherUserPhoto(null));
  }, [chatMeta, currentUser]); // eslint-disable-line

  // Helper: can show photo in chat context (chat participants are always contacts)
  function chatPhotoVisible(photoURL, privacy) {
    if (!photoURL) return false;
    if (privacy === "nobody") return false;
    return true; // contacts/custom: chat participants count as contacts
  }

  // ======================= RENDER =======================

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <MessageCircle size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-4">Please log in to view your chats.</p>
          <Link
            to="/login"
            className="bg-blue-600 text-white px-6 py-2 rounded-full font-medium text-sm"
          >
            Log In
          </Link>
        </div>
      </div>
    );
  }

  // ---- Individual chat view ----
  if (chatId) {
    const myUid = currentUser.uid;
    const otherName = chatMeta
      ? myUid === chatMeta.buyerId
        ? chatMeta.sellerName
        : chatMeta.buyerName
      : "Chat";

    return (
      <div className="flex flex-col h-screen bg-white">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
          <button
            onClick={() => navigate("/chat")}
            className="p-1.5 rounded-full hover:bg-gray-100 transition"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          {/* Other user's avatar in chat header */}
          <div className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-blue-100 flex items-center justify-center">
            {chatPhotoVisible(otherUserPhoto, otherUserPrivacy)
              ? <img src={otherUserPhoto} alt={otherName} className="w-full h-full object-cover" />
              : <span className="text-blue-600 font-bold text-sm">{otherName?.charAt(0)?.toUpperCase() || "?"}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 truncate">{otherName}</p>
            {chatMeta?.listingTitle && (
              <p className="text-xs text-blue-600 truncate flex items-center gap-1">
                <ShoppingBag size={11} />
                {chatMeta.listingTitle}
              </p>
            )}
          </div>
          {chatMeta?.listingId && (
            <Link
              to={`/listing/${chatMeta.listingId}`}
              className="shrink-0"
            >
              {chatMeta.listingImage ? (
                <img
                  src={chatMeta.listingImage}
                  alt={chatMeta.listingTitle}
                  className="w-9 h-9 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                  <ShoppingBag size={16} className="text-gray-400" />
                </div>
              )}
            </Link>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-20 space-y-3">
          {loadingMessages ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageCircle size={40} className="text-gray-200 mb-2" />
              <p className="text-gray-400 text-sm">
                No messages yet. Say hello!
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.senderId === myUid;
              return (
                <div
                  key={msg.id}
                  className={`flex items-end gap-1 ${isMine ? "justify-end" : "justify-start"}`}
                >
                  {/* Reply button (left of others' messages, right of mine) */}
                  {!isMine && (
                    <button
                      onClick={() => setReplyTo(msg)}
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-gray-300 hover:text-blue-400 hover:bg-blue-50 transition mb-1"
                      title="Reply"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                    </button>
                  )}
                  <div
                    className={`max-w-[75%] rounded-2xl text-sm shadow-sm overflow-hidden ${
                      isMine
                        ? "bg-blue-600 text-white rounded-br-sm"
                        : "bg-gray-100 text-gray-800 rounded-bl-sm"
                    }`}
                  >
                    {/* Quoted reply block */}
                    {msg.replyTo && (
                      <div className={`mx-2 mt-2 px-2 py-1.5 rounded-lg border-l-2 text-xs ${isMine ? "bg-blue-500 border-blue-300 text-blue-100" : "bg-gray-200 border-gray-400 text-gray-500"}`}>
                        <p className="font-semibold truncate">{msg.replyTo.senderName}</p>
                        <p className="truncate">{msg.replyTo.type === "image" ? "📷 Photo" : msg.replyTo.text}</p>
                      </div>
                    )}
                    {msg.type === "image" ? (
                      <div>
                        <img src={msg.text} alt="sent" className="max-w-[220px] rounded-2xl object-cover" />
                        <p className={`text-xs px-2 pb-1.5 text-right ${isMine ? "text-blue-200" : "text-gray-400"}`}>{formatTime(msg.createdAt)}</p>
                      </div>
                    ) : (
                      <div className="px-4 py-2.5">
                        <p className="leading-snug break-words">{msg.text}</p>
                        <p className={`text-xs mt-1 text-right ${isMine ? "text-blue-200" : "text-gray-400"}`}>
                          {formatTime(msg.createdAt)}
                          {isMine && msg.read && <span className="ml-1 text-blue-300">✓✓</span>}
                        </p>
                      </div>
                    )}
                  </div>
                  {isMine && (
                    <button
                      onClick={() => setReplyTo(msg)}
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-gray-300 hover:text-blue-400 hover:bg-blue-50 transition mb-1"
                      title="Reply"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                    </button>
                  )}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Send error */}
        {sendError && (
          <div className="sticky bottom-[68px] bg-red-50 border-t border-red-200 px-4 py-2 z-30 flex items-center justify-between">
            <p className="text-red-600 text-xs">{sendError}</p>
            <button onClick={() => setSendError("")} className="text-red-400 text-xs ml-2">✕</button>
          </div>
        )}

        {/* Typing indicator */}
        {Object.keys(typingUsers).length > 0 && (
          <div className="sticky bottom-[68px] px-4 py-1 z-30 flex items-center gap-1.5">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1.5">
              <span className="text-xs text-gray-500 font-medium">{Object.values(typingUsers)[0]} is typing</span>
              <span className="flex gap-0.5">
                {[0,1,2].map(i => (
                  <span key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </span>
            </div>
          </div>
        )}

        {/* Reply preview */}
        {replyTo && (
          <div className="sticky bottom-[68px] bg-blue-50 border-t border-blue-200 px-4 py-2 z-30 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-0.5 h-8 bg-blue-500 rounded-full flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-blue-600 truncate">{replyTo.senderName}</p>
                <p className="text-xs text-gray-600 truncate">{replyTo.type === "image" ? "📷 Photo" : replyTo.text}</p>
              </div>
            </div>
            <button onClick={() => setReplyTo(null)} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Quick replies */}
        <div className="sticky bottom-[68px] bg-white border-t border-gray-100 px-4 pt-2 pb-1 flex items-center gap-2 z-30 overflow-x-auto">
          <button
            type="button"
            onClick={() => sendQuickMessage("Is this available?")}
            disabled={sending}
            className="flex-shrink-0 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-100 transition disabled:opacity-50"
          >
            Is this available?
          </button>
          <button
            type="button"
            onClick={() => sendQuickMessage("Can you share more photos?")}
            disabled={sending}
            className="flex-shrink-0 bg-purple-50 text-purple-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-purple-200 hover:bg-purple-100 transition disabled:opacity-50"
          >
            Share photos?
          </button>
          <button
            type="button"
            onClick={() => sendQuickMessage("What is your best price?")}
            disabled={sending}
            className="flex-shrink-0 bg-orange-50 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-200 hover:bg-orange-100 transition disabled:opacity-50"
          >
            Best price?
          </button>
          <button
            type="button"
            onClick={() => sendQuickMessage("I'll pay cash at meetup 💵")}
            disabled={sending}
            className="flex-shrink-0 bg-green-50 text-green-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-green-200 hover:bg-green-100 transition disabled:opacity-50"
          >
            Cash at meetup 💵
          </button>
          {userProfile?.upiId && (
            <button
              type="button"
              onClick={() => sendQuickMessage(`💳 My UPI ID: ${userProfile.upiId}`)}
              disabled={sending}
              className="flex-shrink-0 bg-indigo-50 text-indigo-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-indigo-200 hover:bg-indigo-100 transition disabled:opacity-50"
            >
              Share UPI 💳
            </button>
          )}
          <button
            type="button"
            onClick={() => sendQuickMessage("Please share your UPI ID for payment 💳")}
            disabled={sending}
            className="flex-shrink-0 bg-yellow-50 text-yellow-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-yellow-200 hover:bg-yellow-100 transition disabled:opacity-50"
          >
            Ask UPI 💳
          </button>
        </div>

        {/* Input row with image upload */}
        <form
          onSubmit={handleSend}
          className="sticky bottom-16 bg-white border-t border-gray-200 px-3 py-3 flex items-center gap-2 z-30"
        >
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
            title="Send image"
            className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-blue-100 hover:text-blue-600 transition disabled:opacity-50"
          >
            {uploadingImage
              ? <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              : <ImageIcon size={18} className="text-gray-500" />}
          </button>
          <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSend} className="hidden" />
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={handleTyping}
            placeholder="Type a message..."
            className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-300 transition"
          />
          <button
            type="submit"
            disabled={!text.trim() || sending}
            className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition"
          >
            <Send size={16} className="text-white ml-0.5" />
          </button>
        </form>
      </div>
    );
  }

  // ---- Conversations list view ----
  const filteredConversations = conversations.filter((chat) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const otherName = getOtherPersonName(chat)?.toLowerCase() || "";
    const title = chat.listingTitle?.toLowerCase() || "";
    const last = chat.lastMessage?.toLowerCase() || "";
    return otherName.includes(q) || title.includes(q) || last.includes(q);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-4 pt-4 pb-14 text-white">
        <h1 className="text-2xl font-bold">Messages</h1>
        <p className="text-blue-200 text-sm mt-0.5">Your conversations</p>
      </div>

      {/* Search bar */}
      <div className="px-4 -mt-6 mb-4 relative z-10">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or listing..."
            className="w-full pl-10 pr-10 py-3.5 bg-white rounded-2xl shadow-md border border-gray-100 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X size={16} className="text-gray-400" />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 pb-24 max-w-lg mx-auto">
        {loadingChats ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <MessageCircle size={56} className="text-gray-200 mb-4" />
            <p className="text-gray-600 font-semibold text-lg mb-1">
              No chats yet
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Start a conversation by messaging a seller on a listing.
            </p>
            <Link
              to="/browse"
              className="bg-blue-600 text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-blue-700 transition"
            >
              Browse Listings
            </Link>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12">
            <Search size={36} className="mx-auto text-gray-200 mb-2" />
            <p className="text-gray-500 text-sm">No results for "{searchQuery}"</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredConversations.map((chat) => {
              const otherName = getOtherPersonName(chat);
              const unreadCount = unreadMap[chat.id] || 0;
              return (
                <button
                  key={chat.id}
                  onClick={() => navigate(`/chat/${chat.id}`)}
                  className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3.5 flex items-center gap-3 hover:shadow-md transition text-left"
                >
                  {/* Avatar */}
                  {(() => {
                    const otherUid = chat.buyerId === currentUser.uid ? chat.sellerId : chat.buyerId;
                    const photo = otherUid ? userPhotos[otherUid] : null;
                    const privacy = otherUid ? (userPrivacy[otherUid] || "everyone") : "everyone";
                    return chatPhotoVisible(photo, privacy)
                      ? <img src={photo} alt={otherName} className="w-11 h-11 rounded-full object-cover shrink-0" />
                      : <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <span className="text-blue-600 font-bold text-base">{otherName ? otherName.charAt(0).toUpperCase() : "?"}</span>
                        </div>;
                  })()}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="font-semibold text-sm text-gray-800 truncate">
                        {otherName || "Unknown User"}
                      </p>
                      <p className="text-xs text-gray-400 shrink-0 ml-2">
                        {formatTime(chat.lastAt)}
                      </p>
                    </div>
                    {chat.listingTitle && (
                      <p className="text-xs text-blue-500 truncate mb-0.5">
                        {chat.listingTitle}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 truncate">
                      {chat.lastMessage || "No messages yet"}
                    </p>
                  </div>

                  {/* Unread badge */}
                  {unreadCount > 0 && (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                      <span className="text-white text-xs font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    </div>
                  )}
                  {/* Delete button */}
                  <button
                    onClick={(e) => deleteChat(chat.id, e)}
                    className="p-1.5 rounded-full hover:bg-red-50 text-gray-300 hover:text-red-500 transition shrink-0"
                  >
                    <Trash2 size={15} />
                  </button>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
